import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuthContext } from './AuthContext';
import { openUpgradePage } from '../services/upgradeActions';
import { UpgradeAccountDialog } from '../components/UpgradeAccountDialog';

/**
 * Global orchestrator for the "Upgrade to Premium" flow (ADR-011). Premium
 * and prompt sync share one Firebase uid, so a purchase must be made by a
 * signed-in account: `startUpgrade()` opens Stripe Checkout directly for
 * signed-in users and gates signed-out users behind an account
 * create/sign-in dialog first, then opens checkout automatically.
 *
 * Mounted once in `Providers` so every surface (popup / side panel /
 * fullscreen / settings) shares the same flow; all busy/error feedback for
 * the upgrade path is rendered here, so entry points stay dumb buttons.
 */

export type UpgradePhase = 'idle' | 'account' | 'openingCheckout' | 'checkoutError';

interface UpgradeFlowContextType {
  /** Begin the premium upgrade. No-op while a flow is already in progress. */
  startUpgrade: () => void;
}

const UpgradeFlowContext = createContext<UpgradeFlowContextType | null>(null);

export function useUpgradeFlow() {
  const context = useContext(UpgradeFlowContext);
  if (!context) {
    throw new Error('useUpgradeFlow must be used within an UpgradeFlowProvider');
  }
  return context;
}

export function UpgradeFlowProvider({ children }: { children: React.ReactNode }) {
  const { currentUser, isCloudEnabled } = useAuthContext();
  const [phase, setPhase] = useState<UpgradePhase>('idle');

  const startUpgrade = useCallback(() => {
    setPhase(current => {
      if (current !== 'idle') return current;
      // Without cloud features there is no account to create; go straight to
      // the checkout attempt, which fails into the visible error state.
      return currentUser || !isCloudEnabled ? 'openingCheckout' : 'account';
    });
  }, [currentUser, isCloudEnabled]);

  // The account step is complete whenever a user exists — whether via the
  // dialog's form, a late auth-state restore, or a sign-in on another
  // surface. Advancing on the state (not a form callback) covers them all.
  useEffect(() => {
    if (phase === 'account' && currentUser) {
      setPhase('openingCheckout');
    }
  }, [phase, currentUser]);

  // Entering `openingCheckout` performs the (idempotent) checkout open.
  useEffect(() => {
    if (phase !== 'openingCheckout') return;
    let stale = false;
    void openUpgradePage().then(opened => {
      if (!stale) {
        setPhase(opened ? 'idle' : 'checkoutError');
      }
    });
    return () => {
      stale = true;
    };
  }, [phase]);

  // Cancel never signs the user out: an account created before cancelling
  // simply persists (it shows up in Settings → Account).
  const close = useCallback(() => setPhase('idle'), []);
  const retry = useCallback(() => setPhase('openingCheckout'), []);

  return (
    <UpgradeFlowContext.Provider value={{ startUpgrade }}>
      {children}
      {phase !== 'idle' && <UpgradeAccountDialog phase={phase} onClose={close} onRetry={retry} />}
    </UpgradeFlowContext.Provider>
  );
}
